import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import { PinHeroInfo, setActivePinModal } from '../../features/utilitySlice';
import { activateMarker } from '../../features/activeMarkers';
import {
  CoeInfoWindowDetails,
  HousingInforWindowDetails,
  SupportGroupInfoWindowDetails,
  ClinicalTrialInfoWindowDetails,
} from './InfoWindowDetails';
import { MMMarkerInfoWindowDetails } from './MMMarkers';
import { UserMarkerInfoWindowDetails } from './UserMarker';

const modalContentSx = {
  width: '100%', maxWidth: '100%', flex: 1, overflow: 'hidden',
  border: 'none',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  background: 'linear-gradient(145deg, rgba(244, 240, 255, 0.82) 0%, rgba(236, 232, 250, 0.78) 50%, rgba(244, 240, 255, 0.82) 100%)',
  boxShadow: '0 8px 32px rgba(101, 110, 255, 0.15), 0 2px 8px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.7)',
}

const MORPH_MS = 420
const EXIT_MS = 340
const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'

type Phase = 'idle' | 'clone' | 'morphing' | 'open' | 'closing-snap' | 'closing'

export const PinInfoModal: React.FC = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(900))
  const activePinModal = useSelector((state: any) => state.utility.activePinModal)

  const [phase, setPhase] = useState<Phase>('idle')
  const lastModalRef = useRef(activePinModal)
  const heroRef = useRef<PinHeroInfo | undefined>(undefined)
  const readyRef = useRef(false)
  const boxRef = useRef<HTMLDivElement>(null)
  // Snapshot of the modal's on-screen rect right before closing
  const openRectRef = useRef<{ l: number; t: number; w: number; h: number } | null>(null)
  // Counter that increments each time a new pin is opened — used as React key
  // to force-remount the morphing div so CSS transitions start fresh.
  const instanceRef = useRef(0)

  // Update hero info synchronously during render so the clone phase
  // always uses the NEW pin's position, not the previous one.
  if (activePinModal?.heroInfo) {
    heroRef.current = activePinModal.heroInfo
    lastModalRef.current = activePinModal
  }

  useEffect(() => {
    if (isMobile && activePinModal) {
      instanceRef.current += 1
      readyRef.current = false

      if (activePinModal.heroInfo) {
        setPhase('clone')
        const raf = requestAnimationFrame(() => {
          requestAnimationFrame(() => setPhase('morphing'))
        })
        // Transition to open right when morph finishes (no extra gap)
        const t = setTimeout(() => {
          setPhase('open')
          readyRef.current = true
        }, MORPH_MS)
        return () => { cancelAnimationFrame(raf); clearTimeout(t) }
      } else {
        setPhase('open')
        const t = setTimeout(() => { readyRef.current = true }, 400)
        return () => clearTimeout(t)
      }
    } else if (!activePinModal && phase !== 'idle') {
      // Capture current rect of the modal before switching to fixed positioning
      if (boxRef.current) {
        const r = boxRef.current.getBoundingClientRect()
        openRectRef.current = { l: r.left, t: r.top, w: r.width, h: r.height }
      }
      // First: snap to fixed position at the current location (no transition)
      setPhase('closing-snap')
    }
  }, [isMobile, activePinModal])

  // Once closing-snap renders (fixed at current rect), kick off the shrink-to-pin
  useEffect(() => {
    if (phase !== 'closing-snap') return
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('closing'))
    })
    const t = setTimeout(() => setPhase('idle'), EXIT_MS + 50)
    return () => { cancelAnimationFrame(raf); clearTimeout(t) }
  }, [phase])

  if (!isMobile || phase === 'idle') return null

  const modalData = activePinModal ?? lastModalRef.current
  const hero = heroRef.current

  function handleClose() {
    if (!readyRef.current) return
    dispatch(setActivePinModal(null))
    dispatch(activateMarker({ id: null, disableHover: false }))
  }

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) handleClose()
  }

  const data = modalData?.data
  const category = modalData?.category
  let content: React.ReactNode = null
  if (data && category) {
    if (category === 'user') {
      content = <MMMarkerInfoWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
    } else if (category === 'ownUser') {
      content = <UserMarkerInfoWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
    } else {
      const t: string = typeof data.type === 'string' ? data.type : (data.type?.type ?? '')
      if (t === 'E' || t === 'C') content = <CoeInfoWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
      else if (t === 'H') content = <HousingInforWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
      else if (t === 'S' || t === 'WALK' || t === 'BSTP') content = <SupportGroupInfoWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
      else if (t.startsWith('TR')) content = <ClinicalTrialInfoWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
      else content = <CoeInfoWindowDetails data={data} onClose={handleClose} containerSx={modalContentSx} />
    }
  }

  // No hero — simple overlay
  if (!hero) {
    return (
      <div onClick={handleBackdropClick} style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: phase === 'open' ? 'auto' : 'none',
      }}>
        <div onClick={handleBackdropClick} style={{
          position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)',
          pointerEvents: phase === 'open' ? 'auto' : 'none',
        }} />
        <div style={{
          position: 'relative', width: '92vw', maxWidth: '92vw', maxHeight: '82vh',
          borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
          pointerEvents: phase === 'open' ? 'auto' : 'none',
        }}>
          {content}
        </div>
      </div>
    )
  }

  // --- Hero morph ---
  const pinSize = hero.iconSize
  const pinL = hero.screenX - pinSize / 2
  const pinT = hero.screenY - pinSize / 2

  const modalW = window.innerWidth * 0.92
  const modalL = (window.innerWidth - modalW) / 2
  const modalH = window.innerHeight * 0.82
  const modalT = (window.innerHeight - modalH) / 2

  const isOpen = phase === 'open'
  const isClosingSnap = phase === 'closing-snap'
  const isClosing = phase === 'closing'
  const isEntering = phase === 'clone' || phase === 'morphing'

  // Determine box position for fixed-position phases
  let boxL: number, boxT: number, boxW: number, boxH: number, boxR: number
  if (phase === 'clone') {
    boxL = pinL; boxT = pinT; boxW = pinSize; boxH = pinSize; boxR = pinSize / 2
  } else if (phase === 'morphing') {
    boxL = modalL; boxT = modalT; boxW = modalW; boxH = modalH; boxR = 12
  } else if (isClosingSnap) {
    // Snap to current on-screen position without transition
    const r = openRectRef.current
    boxL = r?.l ?? modalL; boxT = r?.t ?? modalT; boxW = r?.w ?? modalW; boxH = r?.h ?? modalH; boxR = 12
  } else {
    // closing → shrink to pin
    boxL = pinL; boxT = pinT; boxW = pinSize; boxH = pinSize; boxR = pinSize / 2
  }

  const dur = isClosing ? EXIT_MS : MORPH_MS

  // Cross-fade: during morphing, both pin icon and content are rendered;
  // pin fades out while content fades in during the second half of the morph.
  const renderContent = phase === 'morphing' || isOpen || isClosingSnap
  const contentVisible = isOpen || isClosingSnap || phase === 'morphing'
  const pinIconVisible = phase === 'clone' || phase === 'morphing' || isClosingSnap || isClosing

  return (
    <div onClick={handleBackdropClick} style={{
      position: 'fixed', inset: 0, zIndex: 1300,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: isOpen ? 'auto' : 'none',
    }}>
      {/* Backdrop */}
      <div onClick={handleBackdropClick} style={{
        position: 'absolute', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.35)',
        opacity: (phase === 'morphing' || isOpen || isClosingSnap) ? 1 : 0,
        transition: `opacity ${isClosing ? EXIT_MS : dur}ms ease`,
        pointerEvents: isOpen ? 'auto' : 'none',
      }} />

      {/* Morphing clone / Modal — key forces remount per pin so transitions start fresh */}
      <div key={instanceRef.current} ref={boxRef} style={isOpen ? {
        position: 'relative',
        width: '92vw',
        maxWidth: '92vw',
        maxHeight: '82vh',
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
      } : {
        position: 'fixed',
        left: boxL,
        top: boxT,
        width: boxW,
        height: boxH,
        borderRadius: boxR,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        // No transition for closing-snap (instant), transition for everything else
        transition: isClosingSnap ? 'none' : [
          `left ${dur}ms ${EASING}`,
          `top ${dur}ms ${EASING}`,
          `width ${dur}ms ${EASING}`,
          `height ${dur}ms ${EASING}`,
          `border-radius ${dur}ms ${EASING}`,
          `opacity ${dur}ms ease`,
        ].join(', '),
        opacity: isClosing ? 0 : 1,
        pointerEvents: 'none',
      }}>
        {/* Pin icon — stays visible through the morph, no fade */}
        {pinIconVisible && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
          }}>
            <img src={hero.iconUrl} alt="" style={{
              width: (phase === 'clone' || isClosing) ? pinSize : 100,
              height: (phase === 'clone' || isClosing) ? pinSize : 100,
              objectFit: 'contain',
              transition: `width ${dur}ms ${EASING}, height ${dur}ms ${EASING}`,
            }} />
          </div>
        )}

        {/* Modal content — balloons in from center once morph lands */}
        {renderContent && (
          <div style={{
            display: 'flex', flexDirection: 'column',
            width: '100%', height: '100%', overflow: 'hidden',
            transform: (isOpen || isClosingSnap) ? 'scale(1)' : 'scale(0.3)',
            opacity: (isOpen || isClosingSnap) ? 1 : 0,
            transition: `transform 200ms ${EASING}, opacity 150ms ease`,
          }}>
            {content}
          </div>
        )}
      </div>
    </div>
  )
}
